import { useTranslation } from 'react-i18next';
import { auditEntityLabel } from '@/lib/audit-labels';
import { RecordLink } from './RecordLink';
import { Row, when } from './bodyParts';
import type { RecordType } from './registry';

export interface AuditEntryDetail {
  id: string;
  action: string;
  entity: string;
  entityId?: string | null;
  entityLabel?: string | null;
  entityName?: string | null;
  movementType?: string | null;
  recordType?: string | null;
  createdAt: string;
  userId?: string | null;
  user?: { firstName?: string | null; lastName?: string | null; email?: string | null } | null;
  oldValues?: Record<string, unknown> | null;
  newValues?: Record<string, unknown> | null;
  ipAddress?: string | null;
  userAgent?: string | null;
  hash?: string | null;
}

export const DELETED_RECORD = 'Deleted record';

/** 'JournalEntry' -> 'Journal entry' (the entity class, never a raw key). */
export function humanizeEntity(entity: string): string {
  const s = entity.replace(/([a-z0-9])([A-Z])/g, '$1 $2').toLowerCase();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

const show = (v: unknown) => (v === undefined ? '—' : typeof v === 'string' ? v : JSON.stringify(v));

/** Read-only audit entry for the record viewer (POS-I239). Raw ids live in the collapsed Technical details. */
export function AuditEntryDetailBody({ data }: { data: AuditEntryDetail }) {
  const { t } = useTranslation('records');
  const { t: tInv } = useTranslation('inventory');
  const label = auditEntityLabel(data, tInv);
  const oldV = data.oldValues ?? {};
  const newV = data.newValues ?? {};
  const keys = Array.from(new Set([...Object.keys(oldV), ...Object.keys(newV)])).filter(
    (k) => JSON.stringify(oldV[k]) !== JSON.stringify(newV[k]),
  );
  const who = data.user ? `${data.user.firstName ?? ''} ${data.user.lastName ?? ''}`.trim() || data.user.email : null;
  const record =
    data.recordType && data.entityId && label ? (
      <RecordLink type={data.recordType as RecordType} id={data.entityId} label={label} />
    ) : (
      <span>{data.entityLabel === DELETED_RECORD ? t('deletedRecord') : (label ?? '—')}</span>
    );

  return (
    <div className="space-y-5 text-sm">
      <dl className="space-y-1.5">
        <Row label={t('auditAction')}>{data.action}</Row>
        <Row label={t('auditRecordType')}>{humanizeEntity(data.entity)}</Row>
        <Row label={t('auditRecord')}>{record}</Row>
        <Row label={t('auditUser')}>
          {who ?? t('unknownUser')}
          {data.user?.email && who !== data.user.email && (
            <span className="ms-2 text-xs text-muted-foreground" dir="ltr">{data.user.email}</span>
          )}
        </Row>
        <Row label={t('auditWhen')}><span dir="ltr">{when(data.createdAt)}</span></Row>
      </dl>

      <div>
        <p className="mb-1 text-xs font-medium text-muted-foreground">{t('auditChanges')}</p>
        {keys.length === 0 ? (
          <p className="text-muted-foreground">{t('auditNoChanges')}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b text-xs text-muted-foreground">
                  <th className="py-1 text-start font-medium">{t('auditField')}</th>
                  <th className="py-1 text-start font-medium">{t('auditBefore')}</th>
                  <th className="py-1 text-start font-medium">{t('auditAfter')}</th>
                </tr>
              </thead>
              <tbody>
                {keys.map((k) => (
                  <tr key={k} className="border-b align-top last:border-0">
                    <td className="py-1.5 pe-2 font-medium break-words">{k}</td>
                    <td className="py-1.5 pe-2 break-words text-muted-foreground" dir="auto">{show(oldV[k])}</td>
                    <td className="py-1.5 break-words" dir="auto">{show(newV[k])}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <details className="rounded-md border px-3 py-2" data-testid="record-viewer-technical">
        <summary className="cursor-pointer text-xs font-medium text-muted-foreground">{t('technicalDetails')}</summary>
        <dl className="mt-2 space-y-1.5 text-xs">
          <Row label={t('auditEntityId')}><bdi dir="ltr" className="font-mono">{data.entityId ?? '—'}</bdi></Row>
          <Row label={t('auditUserId')}><bdi dir="ltr" className="font-mono">{data.userId ?? '—'}</bdi></Row>
          <Row label={t('auditIp')}><bdi dir="ltr" className="font-mono">{data.ipAddress ?? '—'}</bdi></Row>
          <Row label={t('auditUserAgent')}><bdi dir="ltr">{data.userAgent ?? '—'}</bdi></Row>
          <Row label={t('auditHash')}><bdi dir="ltr" className="break-all font-mono">{data.hash ?? '—'}</bdi></Row>
        </dl>
      </details>
    </div>
  );
}
